package com.islamicmedia.site.pages

import androidx.compose.runtime.*
import com.islamicmedia.shared.data.api.serverBaseUrl
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.presentation.AuthViewModel
import com.islamicmedia.site.components.EmailVerificationModal
import com.islamicmedia.site.components.InjectSpinKeyframes
import com.islamicmedia.site.theme.*
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.url.URLSearchParams
import org.w3c.fetch.RequestInit

/**
 * Invite landing page — `/invite?t=<token>`.
 *
 * The recipient half of the invitation system (see db/migrations/2026-07-14_invitations.sql).
 * Shows the gift, makes them sign in or sign up, then redeems the token — which binds the
 * membership to the email they ACTUALLY authenticated with, not one we merely hoped for.
 *
 * The token rides in a query param rather than a path segment because Kobweb's static export
 * emits flat files: /invite/<token> would have no HTML to serve, /invite?t=… resolves to
 * invite.html.
 *
 * Redemption fires off `isAuthenticated`, never off "signup succeeded" — on PROD, sign-up
 * returns before the email is confirmed, and only a real session guarantees a bearer token.
 */

private val json = Json { ignoreUnknownKeys = true }

/** Where the claim intent is parked across the Google OAuth round-trip. Value = the invite token,
 *  so a parked intent can't be replayed against a *different* invite. */
private const val CLAIM_INTENT_KEY = "ff_invite_claim_intent"

/** What the server says this invite is worth (GET /api/invite/{token}). */
private data class InviteOffer(
    val status: String,          // pending | redeemed | revoked | expired | not_found
    val durationMonths: Int,
    val mode: String,            // link | email
    val intendedEmail: String?,
)

/** Outcome of POST /api/redeem. */
private data class RedeemResult(
    val status: String,          // ok | not_found | expired | revoked | already_redeemed | email_mismatch | error
    val months: Int = 0,
    val intendedEmail: String? = null,
)

private fun monthsLabel(n: Int) = if (n == 1) "1 month" else "$n months"

@Page
@Composable
fun InvitePage() {
    val token = remember { URLSearchParams(window.location.search).get("t") }
    val serverUrl = remember { serverBaseUrl() }
    val scope = rememberCoroutineScope()

    val authViewModel = remember { AuthViewModel() }
    val authState by authViewModel.uiState.collectAsState()

    var offer by remember { mutableStateOf<InviteOffer?>(null) }
    var offerLoading by remember { mutableStateOf(true) }
    var redeeming by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<RedeemResult?>(null) }
    var prefilled by remember { mutableStateOf(false) }

    /**
     * Has the visitor actually ASKED to claim this gift?
     *
     * An invite is single-use, so redeeming must never be a side effect of merely opening the
     * link. Someone who is already signed in — the admin testing a link, say — would otherwise
     * burn the invite just by looking at it. So redemption waits for intent, and intent comes
     * from exactly one place: pressing a claim button.
     *
     * The OAuth round-trip is the awkward case: Google bounces the browser away and back, and on
     * return "already signed in" and "just signed in to claim" look identical. So we park the
     * intent in sessionStorage before leaving and pick it back up on the way in.
     */
    var claimIntent by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        val parked = window.sessionStorage.getItem(CLAIM_INTENT_KEY)
        if (parked != null && parked == token) claimIntent = true
        window.sessionStorage.removeItem(CLAIM_INTENT_KEY)
    }

    // 1. What is this link worth?
    LaunchedEffect(token) {
        if (token.isNullOrBlank()) {
            offer = InviteOffer("not_found", 0, "link", null)
            offerLoading = false
            return@LaunchedEffect
        }
        offer = fetchOffer(serverUrl, token)
        offerLoading = false
    }

    // Email-mode invites are locked to one address — save them typing it (and guessing which
    // address we meant).
    LaunchedEffect(offer) {
        val intended = offer?.intendedEmail
        if (!prefilled && intended != null && authState.email.isBlank()) {
            authViewModel.onEmailChanged(intended)
            prefilled = true
        }
    }

    // 2. Claim, once there's BOTH a real session and a deliberate request to claim. The session
    //    can arrive any number of ways (sign-in, sign-up with autoconfirm, OTP verification, an
    //    OAuth round-trip); what must never happen is redeeming without [claimIntent].
    LaunchedEffect(authState.isAuthenticated, offer, claimIntent) {
        val live = offer?.status == "pending"
        if (claimIntent && authState.isAuthenticated && live && token != null && result == null && !redeeming) {
            redeeming = true
            result = redeem(serverUrl, token)
            redeeming = false
        }
    }

    PageBackground {
        when {
            offerLoading || authState.isLoading -> Spinner()

            // A dead link never gets as far as the sign-up form.
            offer != null && offer!!.status != "pending" && result == null ->
                DeadInvite(offer!!.status)

            result != null -> RedeemOutcome(
                result = result!!,
                signedInEmail = authState.user?.email,
                onSignOutAndRetry = {
                    // Sign out and come straight back to the SAME invite link, so they land on
                    // the sign-in form with the gift still waiting rather than on the home page.
                    scope.launch {
                        authViewModel.signOut()
                        window.location.href = "/invite" + (token?.let { "?t=$it" } ?: "")
                    }
                },
            )

            redeeming -> Spinner("Claiming your subscription…")

            // Already signed in and just arrived. Do NOT claim behind their back — show whose
            // account the gift would land on and make them press the button.
            authState.isAuthenticated -> AlreadySignedInCard(
                offer = offer,
                email = authState.user?.email,
                onClaim = { claimIntent = true },
                onUseAnotherAccount = {
                    scope.launch {
                        authViewModel.signOut()
                        window.location.href = "/invite" + (token?.let { "?t=$it" } ?: "")
                    }
                },
            )

            // Live invite, not signed in yet → the offer, then the auth card.
            else -> ClaimCard(
                offer = offer,
                viewModel = authViewModel,
                uiState = authState,
                token = token,
                onClaimRequested = { claimIntent = true },
            )
        }
    }

    // PROD requires email confirmation; without this, sign-up dead-ends on a 6-digit code
    // the user is never shown.
    EmailVerificationModal(authViewModel)
}

// ── Networking ──────────────────────────────────────────────────────────────

private suspend fun fetchOffer(serverUrl: String, token: String): InviteOffer {
    return try {
        val resp = window.fetch(
            "$serverUrl/api/invite/$token",
            kotlin.js.json("method" to "GET", "headers" to kotlin.js.json("Accept" to "application/json"))
                .unsafeCast<RequestInit>()
        ).await()
        val obj = json.parseToJsonElement(resp.text().await().toString()) as? JsonObject
            ?: return InviteOffer("error", 0, "link", null)
        fun str(k: String) = (obj[k] as? JsonPrimitive)?.content?.takeIf { it != "null" }
        InviteOffer(
            status = str("status") ?: "error",
            durationMonths = str("durationMonths")?.toIntOrNull() ?: 0,
            mode = str("mode") ?: "link",
            intendedEmail = str("intendedEmail"),
        )
    } catch (e: Exception) {
        console.error("[INVITE] lookup failed: ${e.message}")
        InviteOffer("error", 0, "link", null)
    }
}

private suspend fun redeem(serverUrl: String, token: String): RedeemResult {
    return try {
        // getFreshAccessToken, not accessToken: this is a one-shot write and a stale token
        // would 401 the user out of a gift they can't easily re-trigger.
        val jwt = SupabaseAuthService.instance.getFreshAccessToken()
            ?: return RedeemResult("error")
        val resp = window.fetch(
            "$serverUrl/api/redeem",
            kotlin.js.json(
                "method" to "POST",
                "headers" to kotlin.js.json(
                    "Authorization" to "Bearer $jwt",
                    "Content-Type" to "application/json",
                    "Accept" to "application/json",
                ),
                "body" to """{"token":"$token"}""",
            ).unsafeCast<RequestInit>()
        ).await()
        val obj = json.parseToJsonElement(resp.text().await().toString()) as? JsonObject
            ?: return RedeemResult("error")
        fun str(k: String) = (obj[k] as? JsonPrimitive)?.content?.takeIf { it != "null" }
        RedeemResult(
            status = str("status") ?: "error",
            months = str("months")?.toIntOrNull() ?: 0,
            intendedEmail = str("intended_email"),
        )
    } catch (e: Exception) {
        console.error("[INVITE] redeem failed: ${e.message}")
        RedeemResult("error")
    }
}

// ── UI ──────────────────────────────────────────────────────────────────────

@Composable
private fun PageBackground(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            width(100.percent)
            property("min-height", "100vh")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("background", GradientHeader)
            padding(24.px)
            property("box-sizing", "border-box")
        }
    }) {
        Img(src = "/images/logo.png", attrs = {
            style { width(88.px); height(88.px); marginBottom(16.px) }
        })
        content()
    }
}

@Composable
private fun Card(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            backgroundColor(Color.white)
            borderRadius(24.px)
            property("box-shadow", CardShadow)
            padding(32.px)
            width(100.percent)
            property("max-width", "420px")
            property("box-sizing", "border-box")
        }
    }) { content() }
}

@Composable
private fun Spinner(label: String = "Loading…") {
    Card {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center); gap(14.px)
            }
        }) {
            Div(attrs = {
                style {
                    width(28.px); height(28.px)
                    property("border", "3px solid ${Gray200}")
                    property("border-top-color", "$Primary")
                    borderRadius(50.percent)
                    property("animation", "spin 0.8s linear infinite")
                }
            })
            Div(attrs = { style { fontSize(14.px); color(Gray600) } }) { Text(label) }
        }
    }
    InjectSpinKeyframes()
}

/** Expired / revoked / already-claimed / bogus token. Each gets its own plain-English reason. */
@Composable
private fun DeadInvite(status: String) {
    val (title, body) = when (status) {
        "already_redeemed" -> "This invite has already been used" to
            "Each invitation can only be claimed once. If this was you, just sign in and your subscription is already on your account."
        "expired" -> "This invitation has expired" to
            "Invite links are only good for a limited time. Ask Yusuf for a fresh one and it'll work straight away."
        "revoked" -> "This invitation is no longer valid" to
            "It was cancelled before it was claimed. Ask for a new link if you think that's a mistake."
        else -> "We couldn't find that invitation" to
            "The link may be incomplete. Check that you copied the whole thing, including everything after the '?'."
    }
    Card {
        Heading(title)
        Body(body)
        HomeLink()
    }
}

@Composable
private fun RedeemOutcome(
    result: RedeemResult,
    signedInEmail: String?,
    onSignOutAndRetry: () -> Unit,
) {
    when (result.status) {
        "ok" -> Card {
            Div(attrs = {
                style {
                    width(64.px); height(64.px); borderRadius(50.percent)
                    backgroundColor(PrimaryPale)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    fontSize(32.px); color(Primary)
                    property("margin", "0 auto 18px")
                }
            }) { Text("✓") }
            Heading("You're in!")
            Body(
                "${monthsLabel(result.months).replaceFirstChar { it.uppercase() }} of free access " +
                    "is now on your account${signedInEmail?.let { " ($it)" } ?: ""}. " +
                    "Nothing to pay, nothing to cancel. It just works."
            )
            PrimaryButton("Start analyzing today") { window.location.href = "/" }
            Div(attrs = {
                style {
                    fontSize(13.px); color(Gray500); property("text-align", "center")
                    marginTop(14.px); property("line-height", "1.5")
                }
            }) {
                Text("Using the mobile app? Sign in there with the same email and your subscription will be waiting.")
            }
        }

        // The one failure worth spelling out precisely — a generic error here would leave the
        // recipient with no idea which address to use.
        "email_mismatch" -> Card {
            Heading("This invite is for a different email")
            Body(
                "It was sent to ${result.intendedEmail ?: "another address"}, but you're signed in as " +
                    "${signedInEmail ?: "someone else"}. Sign out and sign back in with " +
                    "${result.intendedEmail ?: "the invited address"} to claim it. " +
                    "Your invitation has not been used up."
            )
            PrimaryButton("Sign out and try again") { onSignOutAndRetry() }
        }

        "already_redeemed" -> Card {
            Heading("Already claimed")
            Body("This invitation has already been used. If it was you, your subscription is on your account. Just open the app.")
            PrimaryButton("Open Fitrah Filter") { window.location.href = "/" }
        }

        "expired", "revoked", "not_found" -> DeadInvite(result.status)

        else -> Card {
            Heading("Something went wrong")
            Body("We couldn't claim your subscription just now. Please try the link again in a moment. Your invitation has not been used up.")
            PrimaryButton("Try again") { window.location.reload() }
        }
    }
}

/**
 * Someone who is ALREADY signed in opened the invite link. Confirm before spending it — an invite
 * is single-use, and silently granting it to whatever account happens to be logged in is how you
 * burn a gift by merely looking at the link.
 */
@Composable
private fun AlreadySignedInCard(
    offer: InviteOffer?,
    email: String?,
    onClaim: () -> Unit,
    onUseAnotherAccount: () -> Unit,
) {
    val months = offer?.durationMonths ?: 0
    Card {
        Div(attrs = {
            style {
                fontSize(13.px); fontWeight(700); color(Primary)
                property("text-transform", "uppercase"); property("letter-spacing", "0.06em")
                property("text-align", "center"); marginBottom(8.px)
            }
        }) { Text("You've been invited") }

        Heading("${monthsLabel(months).replaceFirstChar { it.uppercase() }} free")

        Body(
            "You've been given ${monthsLabel(months)} of free access to Fitrah Filter. " +
                "It'll be added to the account you're signed in with."
        )

        Div(attrs = {
            style {
                backgroundColor(Gray100); borderRadius(12.px)
                padding(10.px, 14.px); marginBottom(20.px)
                fontSize(13.px); color(Gray600); property("text-align", "center")
            }
        }) {
            Text("Signed in as ")
            Span(attrs = { style { fontWeight(600); color(Gray900) } }) { Text(email ?: "your account") }
        }

        PrimaryButton("Claim my ${monthsLabel(months)}") { onClaim() }

        Div(attrs = { style { property("text-align", "center"); marginTop(16.px) } }) {
            Span(attrs = {
                onClick { onUseAnotherAccount() }
                style {
                    color(Primary); fontSize(14.px); cursor("pointer")
                    property("text-decoration", "underline")
                }
            }) { Text("Not you? Use a different account") }
        }
    }
}

/** The live-invite state: what you're getting, then how to claim it. */
@Composable
private fun ClaimCard(
    offer: InviteOffer?,
    viewModel: AuthViewModel,
    uiState: com.islamicmedia.shared.presentation.AuthUiState,
    token: String?,
    onClaimRequested: () -> Unit,
) {
    val months = offer?.durationMonths ?: 0
    val lockedEmail = offer?.intendedEmail

    Card {
        Div(attrs = {
            style {
                fontSize(13.px); fontWeight(700); color(Primary)
                property("text-transform", "uppercase"); property("letter-spacing", "0.06em")
                property("text-align", "center"); marginBottom(8.px)
            }
        }) { Text("You've been invited") }

        Heading("${monthsLabel(months).replaceFirstChar { it.uppercase() }} free")

        Body(
            "You've been given ${monthsLabel(months)} of free access to Fitrah Filter. " +
                "Check any movie, show, or book for your family. " +
                "No card, no charge."
        )

        if (lockedEmail != null) {
            Div(attrs = {
                style {
                    backgroundColor(Gray100); borderRadius(12.px)
                    padding(10.px, 14.px); marginBottom(18.px)
                    fontSize(13.px); color(Gray600); property("text-align", "center")
                }
            }) {
                Text("This invitation is reserved for ")
                Span(attrs = { style { fontWeight(600); color(Gray900) } }) { Text(lockedEmail) }
            }
        }

        Div(attrs = { style { height(1.px); backgroundColor(Gray200); property("margin", "0 0 20px") } })

        Div(attrs = {
            style {
                fontSize(15.px); fontWeight(600); color(Gray900)
                marginBottom(14.px); property("text-align", "center")
            }
        }) {
            Text(if (uiState.isSignUp) "Create your account to claim it" else "Sign in to claim it")
        }

        if (uiState.isSignUp) {
            Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); marginBottom(12.px) } }) {
                Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
                    FieldLabel("First name")
                    TextField(InputType.Text, uiState.firstName, "First", uiState.isSubmitting) {
                        viewModel.onFirstNameChanged(it)
                    }
                }
                Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
                    FieldLabel("Last name")
                    TextField(InputType.Text, uiState.lastName, "Last", uiState.isSubmitting) {
                        viewModel.onLastNameChanged(it)
                    }
                }
            }
        }

        Div(attrs = { style { marginBottom(12.px) } }) {
            FieldLabel("Email")
            TextField(InputType.Email, uiState.email, "you@example.com", uiState.isSubmitting) {
                viewModel.onEmailChanged(it)
            }
        }

        Div(attrs = { style { marginBottom(14.px) } }) {
            FieldLabel("Password")
            TextField(
                InputType.Password, uiState.password, "Choose a password", uiState.isSubmitting,
                onEnter = { onClaimRequested(); viewModel.onSubmit() },
            ) { viewModel.onPasswordChanged(it) }
        }

        uiState.error?.let { err ->
            Div(attrs = {
                style {
                    color(ScoreRed); backgroundColor(ScoreRedBg); borderRadius(8.px)
                    padding(8.px); fontSize(14.px); property("text-align", "center"); marginBottom(14.px)
                }
            }) { Text(err) }
        }

        PrimaryButton(
            label = if (uiState.isSignUp) "Claim my ${monthsLabel(months)}" else "Sign in and claim",
            loading = uiState.isSubmitting,
        ) { onClaimRequested(); viewModel.onSubmit() }

        Div(attrs = {
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                gap(16.px); property("margin", "16px 0")
            }
        }) {
            Div(attrs = { style { property("flex", "1"); height(1.px); backgroundColor(Gray200) } })
            Span(attrs = { style { fontSize(14.px); color(Gray400) } }) { Text("or") }
            Div(attrs = { style { property("flex", "1"); height(1.px); backgroundColor(Gray200) } })
        }

        Button(attrs = {
            onClick {
                // Park the intent: OAuth throws the browser out to Google and back, and on the way
                // in "already signed in" is indistinguishable from "just signed in to claim".
                token?.let { window.sessionStorage.setItem(CLAIM_INTENT_KEY, it) }
                // Hand Supabase THIS page as the return URL. Its JS default is the bare origin,
                // which would strip ?t=… and drop the user on the home page with the gift
                // unclaimed. (This URL must be on Supabase's allowed-redirect list.)
                val back = window.location.origin + "/invite" + (token?.let { "?t=$it" } ?: "")
                viewModel.signInWithGoogle(back)
            }
            style {
                width(100.percent); padding(12.px); fontSize(15.px); fontWeight(500)
                color(Gray700); backgroundColor(Color.white); borderRadius(12.px)
                property("border", "1px solid ${Gray200}")
                cursor("pointer"); property("box-sizing", "border-box")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center); gap(10.px)
            }
            if (uiState.isSubmitting) attr("disabled", "true")
        }) {
            // Same asset the home page's sign-up prompt uses (Index.kt) — keep them identical.
            Img(src = "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg", attrs = {
                style { width(20.px); height(20.px) }
            })
            Span(attrs = { style { fontSize(15.px); fontWeight(600); color(Gray700) } }) {
                Text("Continue with Google")
            }
        }

        Div(attrs = { style { property("text-align", "center"); marginTop(16.px) } }) {
            Span(attrs = {
                onClick { viewModel.toggleSignUp() }
                style {
                    color(Primary); fontSize(14.px); cursor("pointer")
                    property("text-decoration", "underline")
                }
            }) {
                Text(
                    if (uiState.isSignUp) "Already have an account? Sign in"
                    else "Need an account? Sign up"
                )
            }
        }

        // Consent line — governs both Google and email (sign-in-wrap), under all the controls.
        Div(attrs = {
            style {
                fontSize(12.px); color(Gray500); property("text-align", "center")
                property("line-height", "1.5"); marginTop(16.px)
            }
        }) {
            Text("By continuing, you agree to the ")
            A(href = "/terms", attrs = { attr("target", "_blank"); style { color(Primary); fontWeight(600) } }) { Text("Terms of Service") }
            Text(" and ")
            A(href = "/privacy", attrs = { attr("target", "_blank"); style { color(Primary); fontWeight(600) } }) { Text("Privacy Policy") }
            Text(".")
        }
    }
}

// ── Small shared bits ───────────────────────────────────────────────────────

@Composable
private fun Heading(text: String) {
    H1(attrs = {
        style {
            fontSize(24.px); fontWeight(700); color(Gray900)
            property("text-align", "center"); property("margin", "0 0 12px")
        }
    }) { Text(text) }
}

@Composable
private fun Body(text: String) {
    P(attrs = {
        style {
            fontSize(15.px); color(Gray600); property("line-height", "1.55")
            property("text-align", "center"); property("margin", "0 0 20px")
        }
    }) { Text(text) }
}

@Composable
private fun FieldLabel(text: String) {
    Label(attrs = {
        style {
            display(DisplayStyle.Block); fontSize(14.px); fontWeight(500)
            color(Gray700); marginBottom(6.px)
        }
    }) { Text(text) }
}

@Composable
private fun TextField(
    type: InputType<String>,
    value: String,
    hint: String,
    disabled: Boolean,
    onEnter: (() -> Unit)? = null,
    onChange: (String) -> Unit,
) {
    Input(type = type) {
        value(value)
        onInput { onChange(it.value) }
        placeholder(hint)
        if (onEnter != null) onKeyDown { if (it.key == "Enter") onEnter() }
        style {
            width(100.percent); padding(12.px, 16.px); fontSize(16.px)
            borderRadius(12.px); property("border", "1px solid ${Gray300}")
            property("outline", "none"); property("box-sizing", "border-box")
        }
        if (disabled) attr("disabled", "true")
    }
}

@Composable
private fun PrimaryButton(label: String, loading: Boolean = false, onClick: () -> Unit) {
    Button(attrs = {
        onClick { onClick() }
        style {
            width(100.percent); padding(14.px); fontSize(16.px); fontWeight(600)
            color(Color.white); backgroundColor(Primary); borderRadius(12.px)
            property("border", "none"); cursor("pointer"); property("box-sizing", "border-box")
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        }
        if (loading) attr("disabled", "true")
    }) {
        if (loading) {
            Div(attrs = {
                style {
                    width(20.px); height(20.px)
                    property("border", "2px solid rgba(255,255,255,0.3)")
                    property("border-top-color", "white")
                    borderRadius(50.percent)
                    property("animation", "spin 0.8s linear infinite")
                }
            })
        } else Text(label)
    }
    InjectSpinKeyframes()
}

@Composable
private fun HomeLink() {
    Div(attrs = { style { property("text-align", "center") } }) {
        Span(attrs = {
            onClick { window.location.href = "/" }
            style {
                color(Primary); fontSize(14.px); cursor("pointer")
                property("text-decoration", "underline")
            }
        }) { Text("Go to Fitrah Filter") }
    }
}
